import type { CostCenter } from '@src/models/interfaces/costCenter.interface';

export interface CostCenterTreeNode extends CostCenter {
  children?: CostCenterTreeNode[];
  key: string;
}

/** Flat row for ResponsiveTable (no expandable tree support). */
export interface CostCenterTableRow extends CostCenter {
  key: string;
  /** 0 = client / orphan root; 1 = branch under a client. */
  depth: number;
}

/**
 * Builds a two-level tree (client → branch).
 * Orphan branches (missing parent) are shown as roots so they stay editable.
 */
export const buildCostCenterTree = (items: CostCenter[]): CostCenterTreeNode[] => {
  const byId = new Map(items.map(cc => [cc.id, { ...cc, key: cc.id, children: [] as CostCenterTreeNode[] }]));
  const roots: CostCenterTreeNode[] = [];

  byId.forEach(node => {
    if (node.parentId && byId.has(node.parentId)) {
      byId.get(node.parentId)!.children!.push(node);
      return;
    }
    roots.push(node);
  });

  const sortNodes = (nodes: CostCenterTreeNode[]) => {
    nodes.sort((a, b) => a.code.localeCompare(b.code) || a.name.localeCompare(b.name));
    nodes.forEach(n => {
      if (n.children?.length) {
        sortNodes(n.children);
      } else {
        delete n.children;
      }
    });
  };
  sortNodes(roots);
  return roots;
};

/**
 * Depth-first flatten for ResponsiveTable: clients first, then their branches.
 */
export const flattenCostCenterTreeForTable = (
  nodes: CostCenterTreeNode[],
): CostCenterTableRow[] => {
  const rows: CostCenterTableRow[] = [];
  const walk = (list: CostCenterTreeNode[], depth: number) => {
    list.forEach(node => {
      const { children, ...rest } = node;
      rows.push({ ...rest, depth });
      if (children?.length) {
        walk(children, depth + 1);
      }
    });
  };
  walk(nodes, 0);
  return rows;
};

/** Flat selector options: branches listed under their client label. */
export const flattenActiveForSelector = (
  items: CostCenter[],
): Array<CostCenter & { label: string }> => {
  const active = items.filter(cc => cc.active);
  const roots = active.filter(cc => !cc.parentId);
  const byParent = new Map<string, CostCenter[]>();
  active
    .filter(cc => cc.parentId)
    .forEach(cc => {
      const list = byParent.get(cc.parentId!) ?? [];
      list.push(cc);
      byParent.set(cc.parentId!, list);
    });

  const rows: Array<CostCenter & { label: string }> = [];
  roots
    .sort((a, b) => a.code.localeCompare(b.code))
    .forEach(root => {
      rows.push({ ...root, label: `${root.code} · ${root.name}` });
      (byParent.get(root.id) ?? [])
        .sort((a, b) => a.code.localeCompare(b.code))
        .forEach(branch => {
          rows.push({
            ...branch,
            label: `${root.code} › ${branch.code} · ${branch.name}`,
          });
        });
    });

  // Active orphans (parent inactive / missing)
  active
    .filter(cc => cc.parentId && !roots.some(r => r.id === cc.parentId))
    .forEach(cc => {
      if (!rows.some(r => r.id === cc.id)) {
        rows.push({ ...cc, label: `${cc.code} · ${cc.name}` });
      }
    });

  return rows;
};
